import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState } from 'react';
import { InlineNotification, ModalWindow, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { useDataChangeRequestsContext, useDcrApiContext } from '../../contexts';
import { useExecuteDcr } from '../../hooks';
import { getDcrTypeLabel } from '../../utils';
import { DcrSignatureFields, isDcrSignatureComplete } from '../dcr_signature_fields';
import { DcrChangeDetails } from '../dcr_summary';
const Section = ({ label, children }) => (_jsxs("div", { className: 'flex flex-col gap-4 border-t border-neutral-200 py-6 md:flex-row md:gap-8', children: [_jsx(Text, { size: 'sm', className: 'font-semibold md:w-48 md:shrink-0', children: label }), _jsx("div", { className: 'flex flex-1 flex-col gap-4', children: children })] }));
export const DcrExecuteModal = ({ record, onClose }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const apiContext = useDcrApiContext();
    const { userEmail } = useDataChangeRequestsContext();
    const { lastEntities } = useContext(AuthContext);
    const iamClient = useApiClient(ApiClientService.IAM);
    const [signature, setSignature] = useState({ signer: '', password: '' });
    const executeDcr = useExecuteDcr(apiContext);
    const canSubmit = isDcrSignatureComplete(signature) && !executeDcr.isPending;
    const handleSubmit = async () => {
        try {
            // The connector authorizes execution from the live IAM role groups it reads with a
            // fresh IAM token, so the password is exchanged for one rather than sent to the org.
            const loginEntityId = lastEntities.find((entity) => entity.email === userEmail)?.entityId || 1;
            const { data } = await iamClient.post('/v1/login', {
                email: userEmail,
                password: signature.password,
                entityId: loginEntityId,
            });
            if (!data?.token) {
                throw new Error('No token received from IAM login');
            }
            await executeDcr.mutateAsync({
                dcrId: record._id,
                signature: { signer: signature.signer, iamToken: data.token },
            });
            addNotification({ title: translate('Data change request executed'), type: 'success' });
            onClose();
        }
        catch (error) {
            addNotification({
                title: translate('Could not execute the request'),
                subtitle: error?.response?.data?.reason ??
                    error?.response?.data?.message ??
                    translate('Check your credentials and try again.'),
                type: 'error',
            });
        }
    };
    return (_jsx(ModalWindow, { isOpen: true, closeWindow: onClose, width: 'lg', title: translate('Execute data change request'), subtitle: record.dcr_intake__number, footerPrimaryActionButton: {
            label: translate('Execute'),
            onClick: handleSubmit,
            disabled: !canSubmit,
        }, footerSecondaryActionButtons: [{ label: translate('Back'), onClick: onClose, variant: 'secondary' }], dataTestId: 'dcr-execute-modal', children: _jsxs("div", { className: 'flex flex-col', children: [_jsxs(Section, { label: translate('What will happen'), children: [_jsxs(Text, { size: 'sm', className: 'text-neutral-700', children: [translate('The change this request asks for will be made to the study data:'), ' ', getDcrTypeLabel(record.dcr_intake__type), record.dcr_intake__public_user_number ? ` (${record.dcr_intake__public_user_number})` : '', "."] }), _jsx(InlineNotification, { type: 'warning', title: translate('This cannot be undone'), subtitle: translate('The request is resolved once the change has been made.') })] }), _jsx(Section, { label: translate('Details'), children: _jsx(DcrChangeDetails, { type: record.dcr_intake__type, changes: record.dcr_intake__changes ?? {}, emptyMessage: translate('This request type changes no fields of its own.') }) }), _jsx(Section, { label: translate('Sign'), children: _jsx(DcrSignatureFields, { value: signature, email: userEmail, onChange: setSignature, disabled: executeDcr.isPending }) })] }) }));
};
